import { useEffect, useState } from 'react'
import type { ProviderId } from '@/types'

export type HealthState = 'online' | 'degraded' | 'offline' | 'unconfigured' | 'unknown'

export interface ProviderHealth {
  id: ProviderId
  state: HealthState
  latency_ms?: number
  message?: string
  /** Ollama Cloud: count van models met :cloud suffix */
  cloud_models?: number
  first_model?: string | null
  /** Ollama Local: daemon version */
  version?: string | null
  /** Codex: ChatGPT-subscription auth metadata */
  auth_mode?: string
  last_refresh?: string | null
  age_days?: number | null
  checked_at: string
}

export interface HealthSnapshot {
  overall: HealthState
  providers: ProviderHealth[]
  ttl_ms: number
  generated_at: string
}

const POLL_MS = 60_000

/**
 * Polls /api/llm/health every 60s. Returns latest snapshot or null while loading.
 */
export function useHealth(): { data: HealthSnapshot | null; error: string | null } {
  const [data, setData] = useState<HealthSnapshot | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    async function fetchOnce() {
      try {
        const r = await fetch('/api/llm/health', { cache: 'no-store' })
        if (!r.ok) throw new Error(`HTTP ${r.status}`)
        const json = (await r.json()) as HealthSnapshot
        if (!cancelled) {
          setData(json)
          setError(null)
        }
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e))
      }
    }

    fetchOnce()
    const id = setInterval(fetchOnce, POLL_MS)
    return () => {
      cancelled = true
      clearInterval(id)
    }
  }, [])

  return { data, error }
}

export function providerHealthOf(snapshot: HealthSnapshot | null, id: ProviderId): ProviderHealth | null {
  if (!snapshot) return null
  return snapshot.providers.find((p) => p.id === id) ?? null
}
